// ---- Follow-camera dev panel (F7) -------------------------------------------
// A dev-only tool for dialing in the player follow-camera angle: sliders to zoom
// (dolly) in/out and tilt the pitch, a reset-to-default button, and a
// click-and-drag ORBIT on the canvas (horizontal = yaw around the player,
// vertical = pitch). Everything here is CLIENT-VISUAL — it just mutates the
// shared CAM_DEV tuning + game.camYaw (both read by camera.ts updateCamera), so
// there's no server involvement and no wire message (like the F6 toon panel).
//
// Follows toondev.ts's panel/slider scaffolding, parked below the toon panel.
// The drag-orbit is gated on the panel being OPEN: while open the panel OWNS
// left-clicks on the canvas (a capture-phase handler stops them reaching
// focus/pickup), so you tune the camera without accidentally focusing an NPC;
// close it (F7) to play normally.

import { CAM_DEV, resetCamDev } from "./camera.ts";
import { game } from "./state.ts";

let panel: HTMLDivElement;
let readout: HTMLDivElement;
let zoomSlider: HTMLInputElement;
let pitchSlider: HTMLInputElement;
let on = false;

const BTN =
  "font:11px ui-monospace, Menlo, monospace; color:#d8cfe3; background:#1e1c24;" +
  "border:1px solid #443a4a; border-radius:5px; padding:2px 7px; cursor:pointer;";

function button(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  b.style.cssText = BTN;
  b.onclick = onClick;
  return b;
}

function row(parent: HTMLElement, gap = 4): HTMLDivElement {
  const d = document.createElement("div");
  d.style.cssText = `display:flex; flex-wrap:wrap; gap:${gap}px; margin-top:6px; align-items:center;`;
  parent.appendChild(d);
  return d;
}

function slider(
  parent: HTMLElement, label: string, min: number, max: number, step: number,
  value: number, onInput: (v: number) => void,
): HTMLInputElement {
  const wrap = row(parent);
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.style.cssText = "width:52px; display:inline-block;";
  const s = document.createElement("input");
  s.type = "range";
  s.min = String(min); s.max = String(max); s.step = String(step); s.value = String(value);
  s.style.cssText = "flex:1; min-width:120px; accent-color:#9a7fd0;";
  s.oninput = () => { onInput(Number(s.value)); renderReadout(); };
  wrap.append(lab, s);
  return s;
}

// Pitch orbit range (radians): clamps both the slider and the drag so the camera
// can't flip past straight-overhead / straight-behind.
const PITCH_MIN = -0.8, PITCH_MAX = 0.8;

/** Push the current CAM_DEV values back onto the sliders (used after a drag or a
 *  reset changes them from outside the slider oninput). */
function syncSliders(): void {
  zoomSlider.value = String(CAM_DEV.zoom);
  pitchSlider.value = String(CAM_DEV.pitch);
}

export function initCameraDev(canvas: HTMLCanvasElement): void {
  panel = document.createElement("div");
  panel.id = "cameradev";
  panel.style.cssText =
    "position:fixed; top:270px; right:290px; z-index:7; display:none; width:250px;" +
    "font:11px/1.5 ui-monospace, Menlo, monospace; color:#d8cfe3;" +
    "background:rgba(14,12,18,.88); border:1px solid #443a4a; border-radius:8px;" +
    "padding:8px 10px; user-select:none;";
  panel.addEventListener("pointerdown", (e) => e.stopPropagation());

  const title = document.createElement("div");
  title.textContent = "🎥 camera dev (F7)";
  title.style.cssText = "font-weight:600; margin-bottom:2px;";
  panel.appendChild(title);

  const hint = document.createElement("div");
  hint.textContent = "drag the view to orbit · Q/E also yaw";
  hint.style.cssText = "color:#8f7fb0; margin-bottom:2px;";
  panel.appendChild(hint);

  zoomSlider = slider(panel, "zoom", 0.3, 3, 0.05, CAM_DEV.zoom, (v) => (CAM_DEV.zoom = v));
  pitchSlider = slider(panel, "pitch", PITCH_MIN, PITCH_MAX, 0.02, CAM_DEV.pitch, (v) => (CAM_DEV.pitch = v));

  const actions = row(panel);
  actions.appendChild(button("reset", () => {
    resetCamDev();
    game.camYaw = 0; // straight behind — the default follow heading
    syncSliders();
    renderReadout();
  }));

  readout = document.createElement("div");
  readout.style.cssText = "white-space:pre; margin-top:6px; color:#a898c8;";
  panel.appendChild(readout);

  document.body.appendChild(panel);
  wireDragOrbit(canvas);
}

// ---- Click-and-drag orbit ---------------------------------------------------
// Registered capture-phase on the canvas so, while the panel is open, a
// left-press is swallowed before focus.ts / pickup (both bubble-phase on the same
// canvas) can act on it. Horizontal drag spins game.camYaw; vertical drag tilts
// CAM_DEV.pitch. pointermove/up live on window so a drag continues off-canvas.
function wireDragOrbit(canvas: HTMLCanvasElement): void {
  let dragging = false;
  let lastX = 0, lastY = 0;

  canvas.addEventListener("pointerdown", (e) => {
    if (!on || e.button !== 0) return;
    dragging = true;
    lastX = e.clientX; lastY = e.clientY;
    canvas.setPointerCapture?.(e.pointerId);
    e.stopPropagation(); // keep the click off focus/pickup while tuning
    e.preventDefault();
  }, { capture: true });

  addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX, dy = e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    game.camYaw -= dx * 0.005; // drag right → orbit right
    CAM_DEV.pitch = Math.max(PITCH_MIN, Math.min(PITCH_MAX, CAM_DEV.pitch + dy * 0.004)); // drag down → level out
    pitchSlider.value = String(CAM_DEV.pitch);
    renderReadout();
  });

  addEventListener("pointerup", () => { dragging = false; });
}

export function toggleCameraDev(): void {
  on = !on;
  panel.style.display = on ? "block" : "none";
  if (on) { syncSliders(); renderReadout(); }
}

function renderReadout(): void {
  if (!readout) return;
  const f = (x: number, d = 2) => x.toFixed(d);
  readout.textContent =
    `zoom ${f(CAM_DEV.zoom)}  pitch ${f(CAM_DEV.pitch)}\n` +
    `yaw  ${f(game.camYaw)} rad`;
}
